import { type PayloadAction, createSlice } from '@reduxjs/toolkit';

import { DeviceKind, DeviceOutcome, PermissionStatus } from '../core';
import { acquireDevicesThunk } from './acquireDevices.thunk';

/**
 * The cohesive device-acquisition state: the richer {@link DeviceOutcome} per kind
 * (the full failure taxonomy), the ambient permission per kind, raw per-kind presence,
 * plus whether an acquisition is currently in flight.
 *
 * `isAcquiring` is what makes the "Click Allow" overlay *reactive* — it is true only
 * while a `getUserMedia` request is actually pending, rather than being inferred from
 * a permission state of `'prompt'` (the optimistic read that leaves the overlay stuck
 * when no prompt ever appears).
 */
export type DevicesState = {
  microphone: DeviceOutcome | null;
  camera: DeviceOutcome | null;
  isAcquiring: boolean;
  /**
   * Whether the browser sees a microphone / a camera, read from a raw `enumerateDevices()`
   * by `kind`. Per-kind on purpose: the gate needs BOTH (so we warn when either is missing),
   * but we still acquire and show whichever IS present (we don't withhold the prompt or the
   * one working device just because the other is absent). Unlike the enumerated device
   * *list*, this is accurate before permission is granted (pre-permission entries have blank
   * deviceId/label and are filtered out of the list). `null` until first checked.
   */
  hasMicrophone: boolean | null;
  hasCamera: boolean | null;
  /**
   * Ambient browser permission per kind — the single source of truth for "do we have access".
   * Resolved without prompting (Permissions API / enumerate heuristic) and updated when an
   * acquisition settles. Combined with `hasMicrophone`/`hasCamera`, this drives
   * `selectMicrophoneAvailability` / `selectCameraAvailability` (see {@link deriveDeviceAvailability}).
   * `'unknown'` until first resolved.
   */
  microphonePermission: PermissionStatus;
  cameraPermission: PermissionStatus;
  /**
   * Prompts the user closed without answering, per kind, this page session. Chrome and Safari stop
   * asking after a few, silently, so the acquisition thunk reads this to know when they have.
   */
  dismissals: Record<DeviceKind, number>;
};

export const initialDevicesState: DevicesState = {
  microphone: null,
  camera: null,
  isAcquiring: false,
  hasMicrophone: null,
  hasCamera: null,
  microphonePermission: 'unknown',
  cameraPermission: 'unknown',
  dismissals: { microphone: 0, camera: 0 },
};

export const devicesSlice = createSlice({
  name: 'devices',
  initialState: initialDevicesState,
  reducers: {
    outcomeReset: (state, { payload }: PayloadAction<DeviceKind | undefined>) => {
      if (!payload || payload === 'microphone') state.microphone = null;
      if (!payload || payload === 'camera') state.camera = null;
    },
    /** A failure the caller saw on a track it created itself (see `DeviceManager.reportFault`). */
    outcomeReported: (state, { payload }: PayloadAction<{ kind: DeviceKind; outcome: DeviceOutcome }>) => {
      state[payload.kind] = payload.outcome;
    },
    inputDevicePresenceChanged: (state, { payload }: PayloadAction<{ microphone: boolean; camera: boolean }>) => {
      state.hasMicrophone = payload.microphone;
      state.hasCamera = payload.camera;
    },
    /** Record the resolved ambient permission for a kind (from resolution or an acquisition). */
    devicePermissionResolved: (state, { payload }: PayloadAction<{ kind: DeviceKind; status: PermissionStatus }>) => {
      if (payload.kind === 'microphone') state.microphonePermission = payload.status;
      else state.cameraPermission = payload.status;
    },
  },
  // `isAcquiring` is driven by the thunk lifecycle rather than manual start/settle
  // actions, so it can never get stuck `true` no matter how the thunk ends (a throw
  // still hits `rejected`). The overlay that keys off it is therefore always cleared.
  extraReducers: (builder) => {
    builder
      .addCase(acquireDevicesThunk.pending, (state) => {
        state.isAcquiring = true;
      })
      .addCase(acquireDevicesThunk.fulfilled, (state, { payload }) => {
        state.isAcquiring = false;
        (['microphone', 'camera'] as const).forEach((kind) => {
          const outcome = payload[kind];
          if (outcome === undefined) return;
          state[kind] = outcome;
          if (outcome === DeviceOutcome.Dismissed) state.dismissals[kind] += 1;
          if (outcome === DeviceOutcome.Granted) state.dismissals[kind] = 0;
        });
      })
      .addCase(acquireDevicesThunk.rejected, (state) => {
        state.isAcquiring = false;
      });
  },
});

export const devicesReducer = devicesSlice.reducer;
export const { outcomeReset, outcomeReported, inputDevicePresenceChanged, devicePermissionResolved } =
  devicesSlice.actions;
